import { useContext, useState, useCallback } from 'react';
import {
  Option,
  EnumOptionKind,
  AmbassadorProfile,
  Translation,
} from 'src/types';
import { truncate, take, escapeRegExp } from 'lodash-es';
import { Carousel } from 'react-bootstrap';
import { AppRoutes } from 'src/lib/routes';
import Link, { LinkProps } from 'next/link';
import { FiChevronRight, FiChevronLeft } from 'react-icons/fi';
import { BsStarFill } from 'react-icons/bs';
import dayjs from 'dayjs';
import Highlighter from 'react-highlight-words';
import Money from 'src/components/Money';
import ImageWithFallback from './ImageWithFallback';
import useCurrencyExchange from 'src/hooks/useCurrencyExchange';
import useFilter from 'src/hooks/useFilter';
import { isStayOrFlexible } from 'src/helpers';
import { OtherOptionCacheContext } from 'src/contexts/OtherOptionCacheProvider';
import DefaultProfileSvg from 'public/images/svg_icons/default_profile.svg';
import HeartBorderSvg from 'public/images/svg_icons/heart_border.svg';
import useBucketList from 'src/hooks/useBucketList';
import HeartButton from 'src/components/HeartButton';
import calculateEstimatedAffiliateAmount from 'src/lib/calculate_estimated_affiliate_amount';
import BadgeSvg from 'public/images/svg_icons/badge.svg';
import ShareSvg from 'public/images/svg_icons/share.svg';
import AffiliateLinkButton from 'src/components/ambassador/AffiliateLinkButton';
import OptionCardPrice from './OptionCardPrice';
import OptionQuickViewModal from './OptionQuickViewModal';
import SaleSvg from 'public/images/svg_icons/sale.svg';
import { useLocale, useTranslations } from 'next-intl';

type Props = {
  option: Option;
  colClass?: string;
  highlightText?: string;
  isGift?: boolean;
  useHtmlLink?: boolean;
  hidePrice?: boolean;
  isBucketListPage?: boolean;
  shouldSaveScrollPosition?: boolean;
  ambassadorProfile?: AmbassadorProfile;
  crossOutPriceClass?: string;
  reviewCountClass?: string;
};

const CustomLink: React.ReactFCWithChildren<
  LinkProps & { children?: React.ReactNode } & { className?: string }
> = ({ children, className, ...linkProps }) => {
  return (
    <Link legacyBehavior prefetch={false} {...linkProps}>
      <a className={className || ''}>{children}</a>
    </Link>
  );
};

export const OptionBadge: React.FC<{ option: Option }> = ({ option }) => {
  const translation = useTranslations('components.OptionCard')
  return option.virtual ? (
    <div className="ribbon-shield">{translation('virtual')}</div>
  ) : option.ssExclusive ? (
    <div className="ribbon-shield ribbon-shield--green">Seek Sophie {translation('exclusive')}</div>
  ) : option.topRated ? (
    <div className="ribbon-shield ribbon-shield--organe-light">{translation('superhost')}</div>
  ) : option.minPrice['showDiscount?'] ? (
    <div className="ribbon-shield ribbon-shield--white">
      <SaleSvg className="tw-mr-1 tw-h-3 tw-w-3" />
      {translation('sale')}
    </div>
  ) : null;
};

const humanizeDuration = (option: Option, transition: Translation) => {
  return option.kind === EnumOptionKind.shortAdventure
    ? option.durationHours
      ? option.durationHours >= 1
        ? transition('hr', { count: option.durationHours })
        : transition('min', { count: Math.round(option.durationHours * 60) })
      : null
    : isStayOrFlexible(option)
    ? null
    : option.durationDays || option.durationNights
    ? `${option.durationDays && option.durationDays >= 1 ? `${option.durationDays}${transition('d')}` : `1${transition('d')}`}${
        option.durationNights && option.durationNights >= 1 ? `${option.durationNights}${transition('n')}` : `1${transition('n')}`
      }`
    : null;
};

const OptionCard: React.ReactFCWithChildren<Props> = ({
  option,
  colClass,
  highlightText,
  isGift,
  useHtmlLink,
  hidePrice,
  shouldSaveScrollPosition,
  isBucketListPage,
  children,
  ambassadorProfile,
  crossOutPriceClass,
  reviewCountClass,
}) => {
  const locale = useLocale();
  const translation = useTranslations('components.OptionCard')
  const { setCache } = useContext(OtherOptionCacheContext);
  const { filterRecord } = useFilter();
  const { bucketListOptions } = useBucketList();
  const currencyExchange = useCurrencyExchange();
  const [showQuickViewModal, setShowQuickViewModal] = useState(false);
  const isAvailable = option.available === 1;
  const optionName = `${isGift ? `[${translation('gift')}] ` : ''}${option.name}`;
  const LinkOrA = useHtmlLink ? 'a' : CustomLink;
  const duration = humanizeDuration(option, translation);

  const estimatedAmount = calculateEstimatedAffiliateAmount(
    option.minPrice,
    option.commissionRate
  )
  const maxEstimatedAmount =  calculateEstimatedAffiliateAmount(
    { masterPrice: option.maxPrice },
    option.commissionRate,
  )

  const saveScrollPosition = () => {
    if (filterRecord) {
      const cacheKey = `${filterRecord.type}_${filterRecord.id}`;
      setCache((prev) => ({
        ...prev,
        scrollPosition: {
          ...(prev.scrollPosition || {}),
          [cacheKey]: { x: window.scrollX, y: window.scrollY },
        },
      }));
    }
  };

  const trackClick = useCallback(() => {
    if (!filterRecord) return;
    let event: { eventName: string; eventLabel: string } | null = null;

    if (filterRecord.type === 'City') {
      event = {
        eventName: 'city_option_link_click',
        eventLabel: `City#${filterRecord.name} - Option#${option.id}`,
      };
    } else if (filterRecord.type === 'Country') {
      event = {
        eventName: 'country_option_link_click',
        eventLabel: `Country#${filterRecord.name} - Option#${option.id}`,
      };
    } else if (filterRecord.type === 'Category') {
      event = {
        eventName: 'category_option_link_click',
        eventLabel: `Category#${filterRecord.name} - Option#${option.id}`,
      };
    } else if (filterRecord.type === 'Collection') {
      event = {
        eventName: 'collection_option_link_click',
        eventLabel: `Collection#${filterRecord.name} - Option#${option.id}`,
      };
    }

    if (!event) return;
    if (process.env.NEXT_PUBLIC_APP_ENV === 'production') {
      window.gtag('event', event.eventName, {
        event_category: 'link_click',
        event_label: event.eventLabel,
      });
    }
  }, [filterRecord?.id, filterRecord?.type, option.id]);

  return (
    <>
      <div className={`${colClass || 'col-md-4'} tw-mb-12 md:tw-mb-8`}>
        {!isAvailable ? (
          <div className="ribbon-shield">
            <span className="">{translation('soldOut')}</span>
          </div>
        ) : (
          <OptionBadge option={option} />
        )}

        <div className="carousel-activity-sao-image-container">
          <Carousel
            controls={option.images.length > 1}
            prevIcon={
              <div className="carousel-activity-sao-image__btn-inner">
                <FiChevronLeft strokeWidth={2} className="tw-text-brand-navy" />
              </div>
            }
            nextIcon={
              <div className="carousel-activity-sao-image__btn-inner">
                <FiChevronRight strokeWidth={2} className="tw-text-brand-navy" />
              </div>
            }
            indicators={option.images.length > 1}
            className="carousel-activity-sao-image tw-mb-3 carousel slide"
            interval={null}
            pause={false}
            keyboard={false}
            touch={false}
          >
            {option.images.length > 0 ? (
              take(option.images, 8).map((image) => (
                <Carousel.Item key={image.id} className="carousel-item tw-relative">
                  <LinkOrA
                    href={AppRoutes.experienceRoute(
                      { id: option.slugCode },
                      {
                        ...(isGift ? { gift_experience: 1 } : {}),
                        ...(option.isPrivateAccess ? { pc: option.privateAccessCode } : {}),
                      }
                    ).toPath()}
                    passHref
                  >
                    <ImageWithFallback
                      record={image}
                      imgField="webpMdUrl"
                      fill
                      alt={image.caption || optionName}
                      className="tw-border-2 tw-border-brand-green tw-border-solid tw-rounded-lg tw-object-cover tw-cursor-pointer"
                      onClick={() => {
                        trackClick();
                        shouldSaveScrollPosition && saveScrollPosition();
                      }}
                    />
                  </LinkOrA>
                </Carousel.Item>
              ))
            ) : (
              <Carousel.Item className="carousel-item tw-relative">
                <LinkOrA
                  href={AppRoutes.experienceRoute(
                    { id: option.slugCode },
                    {
                        ...(isGift ? { gift_experience: 1 } : {}),
                        ...(option.isPrivateAccess ? { pc: option.privateAccessCode } : {}),
                      }
                  ).toPath()}
                  passHref
                >
                  <ImageWithFallback
                    src={undefined}
                    fill
                    alt={optionName}
                    className="tw-rounded-lg tw-object-cover tw-cursor-pointer"
                    onClick={() => {
                      trackClick();
                      shouldSaveScrollPosition && saveScrollPosition();
                    }}
                  />
                </LinkOrA>
              </Carousel.Item>
            )}
          </Carousel>
          <div
            className="btn-quick-view tw-hidden md:tw-block tw-bg-white tw-py-2 tw-text-center tw-px-6 tw-absolute tw-transform tw-top-52 tw-left-1/2 tw--translate-x-1/2 tw-z-50 tw-rounded-full hover:tw-bg-brand-green tw-cursor-pointer"
            onClick={() => {
              setShowQuickViewModal(true);
            }}
          >
            <div className="tw-font-styleSans tw-font-semibold tw-text-sm tw-leading-none md:tw-leading-none">
              {translation('quickView')}
            </div>
          </div>
        </div>

        <div className="tw-flex tw-items-center tw-mb-2">
          {option.ratedReviewsCount > 0 ? (
            <div>
              <BsStarFill width={20} height={20} className="tw-text-orange tw-mr-1 tw-pb-0.5" />
              <span className="tw-text-orange tw-text-sm tw-font-semibold tw-leading-none">
                {option.reviewRating}{' '}
              </span>
              <span className="tw-leading-none tw-text-sm">({option.ratedReviewsCount})</span>
            </div>
          ) : dayjs(option.createdAt).isAfter(dayjs().subtract(2, 'month')) ? (
            <div>
              <span className="tw-text-orange tw-text-sm tw-font-semibold">{translation('new')}</span>
            </div>
          ) : (
            ''
          )}

          {(option.ratedReviewsCount > 0 ||
            dayjs(option.createdAt).isAfter(dayjs().subtract(2, 'month'))) &&
          duration ? (
            <span className="tw-mx-1.5">|</span>
          ) : null}

          {duration ? (
            <div>
              <span className="tw-text-sm">{duration}</span>
            </div>
          ) : null}

          {Number(option.maxBookingQuantity) > 0 && (
            <div>
              <span className="tw-mx-1.5">|</span>
              <span className="tw-text-sm">{translation('upTo')} {option.maxBookingQuantity} {translation('guests')}</span>
            </div>
          )}
        </div>

        <div className="tw-font-styleSans tw-font-semibold tw-text-lg md:tw-text-xl tw-leading-none md:tw-leading-none tw-mb-2">
          <LinkOrA
            href={AppRoutes.experienceRoute(
              { id: option.slugCode },
              {
                ...(isGift ? { gift_experience: 1 } : {}),
                ...(option.isPrivateAccess ? { pc: option.privateAccessCode } : {}),
              }
            ).toPath()}
          >
            <span
              className="tw-text-brand-navy tw-cursor-pointer"
              onClick={() => {
                trackClick();
                shouldSaveScrollPosition && saveScrollPosition();
              }}
            >
              <span>
                {highlightText ? (
                  <Highlighter
                    searchWords={escapeRegExp(highlightText).split(' ')}
                    textToHighlight={truncate(optionName, { length: 70 })}
                  />
                ) : (
                  truncate(optionName, { length: 70 })
                )}
              </span>
            </span>
          </LinkOrA>
        </div>

        {hidePrice ? null : (
          <>
            <div className="d-flex align-items-center justify-content-between">
              <div>
                <LinkOrA
                  href={AppRoutes.experienceRoute(
                    { id: option.slugCode },
                    {
                      ...(isGift ? { gift_experience: 1 } : {}),
                      ...(option.isPrivateAccess ? { pc: option.privateAccessCode } : {}),
                    }
                  ).toPath()}
                  passHref
                >
                  <span
                    className="tw-font-semibold tw-text-orange tw-cursor-pointer"
                    onClick={() => {
                      trackClick();
                      shouldSaveScrollPosition && saveScrollPosition();
                    }}
                  >
                    <OptionCardPrice option={option} isGift={isGift}/>
                  </span>
                </LinkOrA>
              </div>
            </div>
            {isBucketListPage ? null : ambassadorProfile?.status === 'active' ? (
              <>
                <div className="tw-flex tw-items-center tw-mt-2">
                  <div className="tw-relative tw-mr-2">
                    <BadgeSvg className="tw-h-10 tw-w-10" />
                    <div className="tw-absolute tw-font-medium tw-text-brand-green tw-leading-tight tw-text-3xs tw-w-full tw-text-center tw-top-3.5">
                      {translation('earn')}
                    </div>
                  </div>
                  <div className="tw-text-sm">
                    <div className="tw-mb-1 tw-leading-none">{translation('estimatedEarnings')}:</div>
                    <div className="tw-text-orange tw-leading-none">
                      {translation.rich('priceFrom', {
                        money: (chunks: React.ReactNode) => (
                          <>
                            <Money
                              amount={estimatedAmount}
                              toCurrency={ambassadorProfile.bankAccountCurrency}
                            />
                            {Number(maxEstimatedAmount.amount) > Number(estimatedAmount.amount) ? (
                              <>
                                <span className="tw-mx-1">-</span>
                                <Money
                                  amount={maxEstimatedAmount}
                                  toCurrency={ambassadorProfile.bankAccountCurrency}
                                />
                              </>
                            ) : null}
                          </>
                        ),
                      })}
                    </div>
                  </div>
                </div>
                <AffiliateLinkButton
                  url={AppRoutes.experienceRoute({ id: option.slugCode }).toUrl()}
                  estimatedAmount={currencyExchange({
                    amount: estimatedAmount,
                    toCurrency: ambassadorProfile.bankAccountCurrency,
                  })}
                  maxEstimatedAmount={
                    currencyExchange({
                      amount: maxEstimatedAmount,
                      toCurrency: ambassadorProfile.bankAccountCurrency
                    })
                  }
                >
                  <div className="tw-cursor-pointer tw-mt-2 tw-ml-1">
                    <ShareSvg className="tw-w-4 tw-h-4" />
                    <span className="tw-font-styleSans tw-font-semibold tw-underline tw-text-sm tw-ml-2">
                      {translation('shareLink')}
                    </span>
                  </div>
                </AffiliateLinkButton>
              </>
            ) : (
              <>
                {option.oneLiner ? (
                  <div className="tw-flex tw-mt-2">
                    <DefaultProfileSvg className="tw-text-brand-navy tw-h-4 tw-w-4 tw-mr-1 tw-pt-1" />
                    <div className="tw-text-brand-navy tw-text-sm">“{option.oneLiner}”</div>
                  </div>
                ) : null}
                {option.isPrivateTrip ? (
                  <div className="tw-lowercase tw-inline-block tw-leading-none tw-text-xs tw-px-4 tw-py-2 tw-border tw-text-brand-navy tw-border-brand-green tw-rounded-2xl tw-mt-2 tw-mr-2">
                    {translation('privateTrip')}
                  </div>
                ) : null}
                {option.isInstant ? (
                  <div className="tw-lowercase tw-inline-block tw-leading-none tw-text-xs tw-px-4 tw-py-2 tw-border tw-text-brand-navy tw-border-brand-green tw-rounded-2xl tw-mt-2 tw-mr-2">
                    {translation('instantBook')}
                  </div>
                ) : null}
              </>
            )}
          </>
        )}

        <HeartButton
          isGiftExperience={isGift ? true : false}
          className="tw-absolute tw-top-0 md:tw-right-3.75 tw-right-1 tw-pt-3 tw-pr-3 tw-cursor-pointer tw-transition-transform tw-duration-500 tw-ease-in-out tw-transform hover:tw-scale-110"
          bucketListOption={bucketListOptions.find(
            (o) =>
              o.supplierActivityOptionId === option.id &&
              o.isGiftExperience === (isGift ? true : false)
          )}
          option={option}
          heartBlankIcon={
            <HeartBorderSvg className="tw-align-top tw-h-6 tw-w-6 tw-text-black tw-text-opacity-30" />
          }
          heartFillIcon={<HeartBorderSvg className="tw-align-top tw-h-6 tw-w-6 tw-text-orange" />}
        />
        {children}
      </div>

      {showQuickViewModal && (
        <OptionQuickViewModal
          show={showQuickViewModal}
          isGift={isGift}
          setShow={setShowQuickViewModal}
          option={option}
        />
      )}
    </>
  );
};

export default OptionCard;
